import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../config/firebaseConfig";
import { avatarImages } from "../profilepage/AvatarConfig";
import "./StudentProfilePage.css";

const formatDate = (dateField) => {
  if (!dateField) return "N/A";

  // Handle string dates
  if (typeof dateField === "string") {
    return new Date(dateField).toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }

  // Handle Firestore Timestamp
  if (dateField && dateField.seconds) {
    return new Date(dateField.seconds * 1000).toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }

  if (dateField instanceof Date) {
    return dateField.toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }

  return "N/A";
};

const StudentProfilePage = ({ setPageTitle }) => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [studentData, setStudentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get the return path from location state, or default to gradebook
  const returnPath = location.state?.returnPath || "/gradebook";
  const courseName = location.state?.courseName;

  useEffect(() => {
    setPageTitle("Student Profile");
    fetchStudentProfile();
  }, [studentId, setPageTitle]);

  const fetchStudentProfile = async () => {
    try {
      setLoading(true);
      const getStudentProfileFunction = httpsCallable(
        functions,
        "getStudentProfile"
      );
      const result = await getStudentProfileFunction({ studentId });
      setStudentData(result.data);
    } catch (error) {
      console.error("Error fetching student profile:", error);
      setError("Failed to load student profile");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(returnPath, { replace: true });
  };

  if (loading) {
    return (
      <div className="student-profile-page">
        <div className="loading">Loading student profile...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="student-profile-page">
        <button className="back-button" onClick={handleBack}>
          ← Back to Gradebook
        </button>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (!studentData) {
    return (
      <div className="student-profile-page">
        <button className="back-button" onClick={handleBack}>
          ← Back to Gradebook
        </button>
        <div className="error-message">Student not found</div>
      </div>
    );
  }

  return (
    <div className="student-profile-page">
      <div className="page-header">
        <button className="back-button" onClick={handleBack}>
          ← {courseName ? `Back to ${courseName}` : "Back to Gradebook"}
        </button>
        <h1>Student Profile</h1>
      </div>

      <div className="profile-content">
        <div className="profile-header">
          <div className="profile-info">
            <img
              src={avatarImages[studentData.avatar || "Avatar0"]}
              alt="Student Avatar"
              className="current-avatar"
            />
            <div className="user-details">
              <h2>{`${studentData.firstName || ""} ${
                studentData.lastName || ""
              }`}</h2>
              <p className="user-role">{studentData.role || "Student"}</p>
              <p className="user-email">
                {studentData.email || "No email provided"}
              </p>
              <div className="user-dates">
                <p>Registered: {formatDate(studentData.registered)}</p>
                <p>Last Login: {formatDate(studentData.lastLogin)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentProfilePage;
